/**
 * FNOL agent notification email: strict ticket layout + Notes overflow.
 * @module claims/agentEmailDetailRows
 */

import {
  getMultiVehicleDescribeParts,
  getSingleVehicleDescribeParts,
  isFireParkedLocationGateSatisfied,
  isTheftParkedLocationGateSatisfied,
  isWeatherParkedLocationGateSatisfied,
  pickMvDriverLicenceFieldsForClaimSubmit,
  type ClaimFlowDraft,
} from './claimDraftStorage'
import {
  formatIncidentReviewDateTime,
  formatIncidentReviewLocation,
} from './claimFlowModel'
import {
  countOtherPartyDriversWithData,
  otherPartyDriverHasAnyData,
  readOtherPartyDriverFromDraft,
  type MultiVehicleOtherPartyDriverIndex,
} from './multiVehicleOtherPartyDrivers'
import {
  TRUSTED_WINDSCREEN_REPAIRER_CHOICE_LABEL,
  trustedWindscreenRepairerCompanyLabel,
} from './windscreenRepairerConstants'
import { resolveAgentEmailLossType } from './fnolAgentEmailLossType'

export type AgentEmailClaimKind =
  | 'windscreen'
  | 'weather'
  | 'theft'
  | 'fire'
  | 'malicious_damage'
  | 'other_loss'
  | 'multi_vehicle'
  | 'single_vehicle'

const BLANK = ''

const NO_DRIVER_STEP_KINDS: ReadonlySet<AgentEmailClaimKind> = new Set([
  'windscreen',
  'malicious_damage',
  'other_loss',
])

function yesNo(v: boolean | null | undefined): string {
  if (v === true) return 'Yes'
  if (v === false) return 'No'
  return BLANK
}

function parkedAtIncidentValue(draft: ClaimFlowDraft, kind: AgentEmailClaimKind): string {
  if (
    kind === 'windscreen' ||
    kind === 'multi_vehicle' ||
    kind === 'single_vehicle' ||
    kind === 'malicious_damage' ||
    kind === 'other_loss'
  ) {
    return BLANK
  }
  if (kind === 'weather') return yesNo(draft.weatherVehicleParkedAtIncident)
  if (kind === 'fire') return yesNo(draft.fireVehicleParkedAtIncident)
  if (kind === 'theft') return yesNo(draft.theftVehicleParkedAtIncident)
  return BLANK
}

function isParkedGate(draft: ClaimFlowDraft, kind: AgentEmailClaimKind): boolean {
  if (kind === 'weather') return isWeatherParkedLocationGateSatisfied(draft)
  if (kind === 'fire') return isFireParkedLocationGateSatisfied(draft)
  if (kind === 'theft') return isTheftParkedLocationGateSatisfied(draft)
  return false
}

function listedDriverValue(draft: ClaimFlowDraft, kind: AgentEmailClaimKind): string {
  if (NO_DRIVER_STEP_KINDS.has(kind)) return BLANK
  if (isParkedGate(draft, kind)) return BLANK
  const mode = draft.multiVehicleDriverCaptureMode
  if (mode === 'manual') return 'No'
  if (mode === 'primary' || mode === 'listed') return 'Yes'
  // Fallback when capture mode not persisted yet (match collision submit inference).
  if (draft.multiVehiclePrimaryDriverDriving === true) return 'Yes'
  if (draft.multiVehicleSecondaryOnPolicyDriving === true) return 'Yes'
  if (
    draft.multiVehiclePrimaryDriverDriving === false &&
    draft.multiVehicleSecondaryOnPolicyDriving === false
  ) {
    return 'No'
  }
  return BLANK
}

function driverBlock(
  draft: ClaimFlowDraft,
  kind: AgentEmailClaimKind
): {
  listedDriver: string
  name: string
  dob: string
  licence: string
  yearsLicensed: string
  gender: string
  licenceExpiry: string
} {
  const empty = {
    listedDriver: BLANK,
    name: BLANK,
    dob: BLANK,
    licence: BLANK,
    yearsLicensed: BLANK,
    gender: BLANK,
    licenceExpiry: BLANK,
  }
  if (NO_DRIVER_STEP_KINDS.has(kind)) return empty
  if (isParkedGate(draft, kind)) return { ...empty, listedDriver: listedDriverValue(draft, kind) }
  if (
    kind === 'multi_vehicle' &&
    (draft.multiVehicleDriverCaptureMode === 'none_unattended' ||
      draft.multiVehicleAttendedAtCollision === false)
  ) {
    return empty
  }
  const licence = pickMvDriverLicenceFieldsForClaimSubmit(draft)
  const name = [draft.multiVehicleDriverFirstName?.trim(), draft.multiVehicleDriverLastName?.trim()]
    .filter(Boolean)
    .join(' ')
  const years =
    typeof licence.multiVehicleYearsLicensed === 'number' &&
    Number.isInteger(licence.multiVehicleYearsLicensed)
      ? String(licence.multiVehicleYearsLicensed)
      : BLANK
  return {
    listedDriver: listedDriverValue(draft, kind),
    name,
    dob: licence.multiVehicleDriverDobDdMmYyyy?.trim() ?? BLANK,
    licence: licence.multiVehicleLicenseNumber?.trim() ?? BLANK,
    yearsLicensed: years,
    gender: draft.multiVehicleDriverGender?.trim() ?? BLANK,
    licenceExpiry: licence.multiVehicleLicenseExpiryDdMmYyyy?.trim() ?? BLANK,
  }
}

function whatHappenedValue(draft: ClaimFlowDraft, kind: AgentEmailClaimKind): string {
  if (kind === 'windscreen') return draft.windscreenDescription?.trim() ?? BLANK
  if (kind === 'single_vehicle') {
    return getSingleVehicleDescribeParts(draft).incident || BLANK
  }
  return getMultiVehicleDescribeParts(draft).incident || BLANK
}

function vehicleDamageValue(draft: ClaimFlowDraft, kind: AgentEmailClaimKind): string {
  if (kind === 'windscreen') return BLANK
  if (kind === 'single_vehicle') {
    return getSingleVehicleDescribeParts(draft).damage || BLANK
  }
  return getMultiVehicleDescribeParts(draft).damage || BLANK
}

function windscreenChoiceOfRepairer(draft: ClaimFlowDraft): string {
  if (draft.windscreenReplacedAlready === true) return BLANK
  const opt = draft.windscreenRepairOption
  if (opt === 'trusted') return TRUSTED_WINDSCREEN_REPAIRER_CHOICE_LABEL
  if (opt === 'own') return 'Own repairer'
  return BLANK
}

function windscreenReplacedBy(draft: ClaimFlowDraft): string {
  if (draft.windscreenReplacedAlready !== true) return BLANK
  if (draft.windscreenRepairOption === 'trusted') {
    return trustedWindscreenRepairerCompanyLabel(draft.windscreenRepairerCompany)
  }
  return draft.windscreenRepairerCompany?.trim() || BLANK
}

function includesThirdPartyBlock(draft: ClaimFlowDraft, kind: AgentEmailClaimKind): boolean {
  if (kind === 'multi_vehicle') return true
  if (kind === 'single_vehicle') return draft.singleVehicleCollisionTarget === 'third_party_property'
  return false
}

const MULTI_VEHICLE_OTHER_PARTY_INDICES: readonly MultiVehicleOtherPartyDriverIndex[] = [1, 2, 3]

function multiVehicleOtherPartyDetailsKnownLabel(index: MultiVehicleOtherPartyDriverIndex): string {
  return `Third Party ${index} details known`
}

function multiVehicleOtherPartyFieldLabel(
  index: MultiVehicleOtherPartyDriverIndex,
  field: 'Name' | 'Address' | 'Mobile' | 'Vehicle Registration'
): string {
  return `Third Party ${index} ${field}`
}

function multiVehicleOtherPartyDetailsKnownValue(
  draft: ClaimFlowDraft,
  index: MultiVehicleOtherPartyDriverIndex
): string {
  if (index === 1) return yesNo(draft.multiVehicleOtherPartyDetailsKnown)
  if (!otherPartyDriverHasAnyData(readOtherPartyDriverFromDraft(draft, index))) return BLANK
  return 'Yes'
}

function multiVehicleOtherPartyFieldValue(
  draft: ClaimFlowDraft,
  index: MultiVehicleOtherPartyDriverIndex,
  field: 'name' | 'address' | 'phone' | 'registration'
): string {
  const detailsKnown =
    index === 1 ? draft.multiVehicleOtherPartyDetailsKnown === true : true
  if (!detailsKnown) return BLANK
  const p = readOtherPartyDriverFromDraft(draft, index)
  switch (field) {
    case 'name':
      return p.name.trim()
    case 'address':
      return p.address.trim()
    case 'phone':
      return p.phone.trim()
    case 'registration':
      return p.registration.trim()
  }
}

/** Per-party third-party rows for multi-vehicle collision agent emails. */
function multiVehicleThirdPartyRows(draft: ClaimFlowDraft): Record<string, string> {
  const count = countOtherPartyDriversWithData(draft)
  const rows: Record<string, string> = {
    'Number of Third party drivers involved': count > 0 ? String(count) : BLANK,
  }

  for (const index of MULTI_VEHICLE_OTHER_PARTY_INDICES) {
    const detailsKnownValue = multiVehicleOtherPartyDetailsKnownValue(draft, index)
    if (detailsKnownValue !== BLANK) {
      rows[multiVehicleOtherPartyDetailsKnownLabel(index)] = detailsKnownValue
    }

    if (!otherPartyDriverHasAnyData(readOtherPartyDriverFromDraft(draft, index))) continue

    const name = multiVehicleOtherPartyFieldValue(draft, index, 'name')
    const address = multiVehicleOtherPartyFieldValue(draft, index, 'address')
    const mobile = multiVehicleOtherPartyFieldValue(draft, index, 'phone')
    const registration = multiVehicleOtherPartyFieldValue(draft, index, 'registration')

    rows[multiVehicleOtherPartyFieldLabel(index, 'Name')] = name
    rows[multiVehicleOtherPartyFieldLabel(index, 'Address')] = address
    rows[multiVehicleOtherPartyFieldLabel(index, 'Mobile')] = mobile
    rows[multiVehicleOtherPartyFieldLabel(index, 'Vehicle Registration')] = registration
  }

  return rows
}

function thirdPartyRows(draft: ClaimFlowDraft, kind: AgentEmailClaimKind): Record<string, string> {
  if (!includesThirdPartyBlock(draft, kind)) return {}
  if (kind === 'multi_vehicle') {
    return multiVehicleThirdPartyRows(draft)
  }
  return {
    'Third Party involved': 'Yes',
    'Third party details known': BLANK,
    'Third party name': BLANK,
    'Third Party address': BLANK,
  }
}

function windscreenExtraRows(draft: ClaimFlowDraft, kind: AgentEmailClaimKind): Record<string, string> {
  if (kind !== 'windscreen') return {}
  return {
    Chip: yesNo(draft.windscreenSmallChip),
    'already replaced y/n': yesNo(draft.windscreenReplacedAlready),
    'Replaced by': windscreenReplacedBy(draft),
    'Choice of repairer': windscreenChoiceOfRepairer(draft),
  }
}

/**
 * Supplementary FNOL detail not in the strict Freshdesk field list (multiline).
 */
export function buildAgentEmailNotesFromDraft(
  draft: ClaimFlowDraft,
  kind: AgentEmailClaimKind
): string {
  const lines: string[] = []

  if (kind === 'malicious_damage' || kind === 'other_loss') {
    lines.push('No driver details captured in portal (incident and damage only)')
  }

  if (kind === 'windscreen') {
    const gst = draft.windscreenGstRegistered
    if (gst === true || gst === false) lines.push(`GST registered: ${gst ? 'Yes' : 'No'}`)
    if (gst === true) {
      const itc = draft.windscreenItcEntitled
      if (itc === true || itc === false) lines.push(`Entitled to ITC: ${itc ? 'Yes' : 'No'}`)
      const pct = draft.windscreenItcPercentage
      if (itc === true && typeof pct === 'number' && Number.isInteger(pct)) {
        lines.push(`ITC percentage: ${pct}%`)
      }
    }
    const tow = draft.requiresTowing
    if (tow === true || tow === false) lines.push(`Requires towing: ${tow ? 'Yes' : 'No'}`)
    const email = draft.windscreenRepairerEmail?.trim()
    const phone = draft.windscreenRepairerPhone?.trim()
    if (email) lines.push(`Repairer email: ${email}`)
    if (phone) lines.push(`Repairer phone: ${phone}`)
    if (draft.windscreenRepairOption === 'trusted' && draft.windscreenReplacedAlready !== true) {
      lines.push(
        `Repairer company: ${trustedWindscreenRepairerCompanyLabel(draft.windscreenRepairerCompany)}`
      )
    } else if (draft.windscreenRepairOption === 'own' && draft.windscreenReplacedAlready !== true) {
      const co = draft.windscreenRepairerCompany?.trim()
      if (co) lines.push(`Repairer company: ${co}`)
    }
  }

  if (kind === 'single_vehicle') {
    if (draft.singleVehicleCollisionTarget === 'other') {
      const other = draft.singleVehicleCollisionTargetOtherText?.trim()
      if (other) {
        lines.push(`Describe what you collided with: ${other}`)
      }
    }
    appendDriverContextNotes(lines, draft)
  }

  if (kind === 'theft') {
    const vehicleStolen = draft.theftVehicleStolenAtIncident
    if (vehicleStolen === true || vehicleStolen === false) {
      lines.push(`Vehicle stolen: ${vehicleStolen ? 'Yes' : 'No'}`)
    }
    const itemsStolen = draft.theftItemsStolenFromVehicle
    if (vehicleStolen === false && (itemsStolen === true || itemsStolen === false)) {
      lines.push(`Items stolen from vehicle: ${itemsStolen ? 'Yes' : 'No'}`)
    } else if (
      vehicleStolen == null &&
      (draft.theftSomethingStolenAtIncident === true || draft.theftSomethingStolenAtIncident === false)
    ) {
      lines.push(
        `Items stolen from vehicle: ${draft.theftSomethingStolenAtIncident ? 'Yes' : 'No'}`
      )
    }
    appendDriverContextNotes(lines, draft)
  }

  if (kind === 'weather' || kind === 'fire') {
    appendDriverContextNotes(lines, draft)
  }

  return lines.join('\n')
}

function appendDriverContextNotes(lines: string[], draft: ClaimFlowDraft): void {
  const mode = draft.multiVehicleDriverCaptureMode
  if (mode === 'primary') lines.push('Driver on policy: Primary policy driver')
  else if (mode === 'listed') lines.push('Driver on policy: Listed driver')
  else if (mode === 'manual') lines.push('Driver on policy: Not listed on policy')
}

/** Row labels in Freshdesk email order (server may inject policy number, claim number, portal UID). */
export const FNOL_AGENT_EMAIL_ROW_ORDER = [
  'Policy Number',
  'Claim Number',
  'Policy Type',
  'Loss type',
  'Incident date and time',
  'Incident location',
  'Was the vehicle parked',
  'Listed Driver',
  'Driver name',
  'Driver DOB',
  'Licence number',
  'Years licensed',
  'Driver gender',
  'Licence expiry',
  'What happened',
  'Vehicle Damage',
  'Notes',
  'Third Party involved',
  'Number of Third party drivers involved',
  'Third Party 1 details known',
  'Third Party 1 Name',
  'Third Party 1 Address',
  'Third Party 1 Mobile',
  'Third Party 1 Vehicle Registration',
  'Third Party 2 details known',
  'Third Party 2 Name',
  'Third Party 2 Address',
  'Third Party 2 Mobile',
  'Third Party 2 Vehicle Registration',
  'Third Party 3 details known',
  'Third Party 3 Name',
  'Third Party 3 Address',
  'Third Party 3 Mobile',
  'Third Party 3 Vehicle Registration',
  'Third party details known',
  'Third party name',
  'Third Party address',
  'Chip',
  'already replaced y/n',
  'Replaced by',
  'Choice of repairer',
  'Portal UID (for audit purposes only)',
] as const

/**
 * Builds strict-layout rows for agent FNOL emails from the session draft.
 * Policy number, claim number, and portal UID are placeholders until the API finalizes the send.
 */
export function buildAgentEmailDetailRowsFromDraft(
  draft: ClaimFlowDraft,
  kind: AgentEmailClaimKind,
  options?: {
    policyTypeLabel?: string
    notes?: string
  }
): Record<string, string> {
  const driver = driverBlock(draft, kind)
  const notes = options?.notes ?? buildAgentEmailNotesFromDraft(draft, kind)

  const core: Record<string, string> = {
    'Policy Number': BLANK,
    'Claim Number': BLANK,
    'Policy Type': options?.policyTypeLabel?.trim() ?? BLANK,
    'Loss type': resolveAgentEmailLossType(draft, kind),
    'Incident date and time': formatIncidentReviewDateTime(draft),
    'Incident location': formatIncidentReviewLocation(draft),
    'Was the vehicle parked': parkedAtIncidentValue(draft, kind),
    'Listed Driver': driver.listedDriver,
    'Driver name': driver.name,
    'Driver DOB': driver.dob,
    'Licence number': driver.licence,
    'Years licensed': isParkedGate(draft, kind) ? BLANK : driver.yearsLicensed,
    'Driver gender': driver.gender,
    'Licence expiry': driver.licenceExpiry,
    'What happened': whatHappenedValue(draft, kind),
    'Vehicle Damage': vehicleDamageValue(draft, kind),
    Notes: notes,
    'Portal UID (for audit purposes only)': BLANK,
    ...thirdPartyRows(draft, kind),
    ...windscreenExtraRows(draft, kind),
  }

  const ordered: Record<string, string> = {}
  for (const label of FNOL_AGENT_EMAIL_ROW_ORDER) {
    if (label in core) ordered[label] = core[label]
  }
  return ordered
}

/** Rows posted on FNOL submit (`agentEmailDetailRows`); API fills policy number, claim number, portal UID. */
export function buildFnolSubmitAgentEmailRows(
  draft: ClaimFlowDraft,
  kind: AgentEmailClaimKind,
  policyTypeQuery: string | null | undefined
): Record<string, string> {
  const pt = policyTypeQuery?.trim().toLowerCase()
  const policyTypeLabel =
    pt === 'camper' ? 'Camper Trailer' : pt === 'caravan' ? 'CARAVAN' : '4X4'
  return buildAgentEmailDetailRowsFromDraft(draft, kind, { policyTypeLabel })
}
